import { create } from 'zustand';
import type { Version } from '@/features/versions/types';
import type { DuplicateChannelInfo } from '@/types/duplicateChannels';

interface ChangeFilters {
  entityType: 'all' | 'channel' | 'plan' | 'category';
  changeType: 'all' | 'create' | 'update' | 'delete';
  dateRange?: {
    start: Date;
    end: Date;
  };
}

interface CopyProgress {
  plans: boolean;
  categories: boolean;
  channels: boolean;
  completed: boolean;
}

interface VersionState {
  selectedVersion: Version | null;
  compareVersion: Version | null;
  isCompareMode: boolean;
  isCreateModalOpen: boolean;
  isPublishModalOpen: boolean;
  isDuplicateChannelModalOpen: boolean;
  duplicateChannels: DuplicateChannelInfo[];
  pendingVersionData: any | null;
  activeTab: string;
  expandedRows: Record<string, boolean>;
  showChanges: boolean;
  selectedChangeId: string | null;
  changeFilters: ChangeFilters;
  copyProgress: CopyProgress | null;
  canEditVersion: boolean;
}

interface VersionActions {
  setSelectedVersion: (version: Version | null) => void;
  setCompareVersion: (version: Version | null) => void;
  setCompareMode: (enabled: boolean) => void;
  setActiveTab: (tab: string) => void;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openPublishModal: () => void;
  closePublishModal: () => void;
  openDuplicateChannelModal: (channels: DuplicateChannelInfo[], versionData: any) => void;
  closeDuplicateChannelModal: () => void;
  setChangeFilters: (filters: Partial<ChangeFilters>) => void;
  setSelectedChangeId: (id: string | null) => void;
  toggleShowChanges: () => void;
  setShowChanges: (show: boolean) => void;
  setCopyProgress: (progress: CopyProgress | null) => void;
  setCanEditVersion: (canEdit: boolean) => void;
}

const initialChangeFilters: ChangeFilters = {
  entityType: 'all',
  changeType: 'all'
};

export const useVersionStore = create<VersionState & VersionActions>((set) => ({
  // State
  selectedVersion: null,
  compareVersion: null,
  isCompareMode: false,
  isCreateModalOpen: false,
  isPublishModalOpen: false,
  isDuplicateChannelModalOpen: false,
  duplicateChannels: [],
  pendingVersionData: null,
  activeTab: 'plans',
  expandedRows: {},
  showChanges: false,
  selectedChangeId: null,
  changeFilters: initialChangeFilters,
  copyProgress: null,
  canEditVersion: false,

  // Actions
  setSelectedVersion: (version) => set({ selectedVersion: version }),
  setCompareVersion: (version) => set({ compareVersion: version }),
  setCompareMode: (enabled) => set({ isCompareMode: enabled }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  openCreateModal: () => set({ isCreateModalOpen: true }),
  closeCreateModal: () => set({ isCreateModalOpen: false }),
  openPublishModal: () => set({ isPublishModalOpen: true }),
  closePublishModal: () => set({ isPublishModalOpen: false }),
  openDuplicateChannelModal: (channels, versionData) => set({ 
    isDuplicateChannelModalOpen: true,
    duplicateChannels: channels,
    pendingVersionData: versionData
  }),
  closeDuplicateChannelModal: () => set({ 
    isDuplicateChannelModalOpen: false,
    duplicateChannels: [],
    pendingVersionData: null
  }),
  setChangeFilters: (filters) => set((state) => ({
    changeFilters: { ...state.changeFilters, ...filters }
  })),
  setSelectedChangeId: (id) => set({ selectedChangeId: id }),
  toggleShowChanges: () => set((state) => ({ showChanges: !state.showChanges })),
  setShowChanges: (show) => set({ showChanges: show }),
  setCopyProgress: (progress) => set({ copyProgress: progress }),
  setCanEditVersion: (canEdit) => set({ canEditVersion: canEdit })
})); 
